import { Button, CheckboxImage } from '@design-system';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { buildSubscriptions } from '@utils/subscriptions';
import classNames from 'classnames';
import { isPast } from 'date-fns';
import { TFunction } from 'i18next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { AuthEntryPoint } from '../../../../features/auth/types';
import { PreAuthDrawButton } from '../../../../features/draws/components/PreAuthDrawButton';
import { DrawWithFrames } from '../../../../features/draws/types';
import { buildDrawFlowConfig } from '../../../../features/draws/utils/useDrawFlow';
import { pathFor } from '../../../../helpers/links';
import { skuRef } from '../../../../helpers/purchasing';
import { useIsSubscribed } from '../../../../hooks/subscription';
import { useAuthenticate } from '../../../../hooks/useAuthenticate';
import { getDrawStage } from '../../../../hooks/useDrawStage';
import { isPreOrder, useIsPreOrder } from '../../../../hooks/useIsPreOrder';
import { FlowConfig, useAuth, useFlow } from '../../../../providers';
import { useDraws } from '../../../../providers/DrawProvider';
import { usePreAuthDrawCtx } from '../../../../providers/PreAuthDrawProvider';
import { useUpsertMultisetPreferencesDrawMutation } from '../../../../types/generated';
import { Image } from '../../../Image';
import { AML_LIMIT } from '../../../../constants/product';

const SelectWorks = ({
  draws,
  onEnterDraw,
  t,
}: {
  draws: DrawWithFrames[];
  onEnterDraw: (
    drawWithFrames: DrawWithFrames,
    relaunchMultiFlowOnCompletion: boolean,
  ) => void;
  t: TFunction;
}) => {
  const { goToStep } = useFlow();
  const { drawIdsEntered, selectDraw, selectedDraws } = useDraws();
  const selectAll = useCallback(() => {
    draws
      .filter((draw) => !selectedDraws.includes(draw.draw.id))
      .forEach((draw) => selectDraw(draw.draw.id));
  }, [selectDraw, draws, selectedDraws]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between">
        <p>{t('drawers.review.draw.multiProduct.selectWorkDescription')}</p>
      </div>

      <div className="flex justify-between">
        <p className="font-bold">
          {draws.length} {t('drawers.review.releases.variants')}
        </p>
        <button className="hyperlink" onClick={selectAll} type="button">
          {t('drawers.review.releases.selectAll')}
        </button>
      </div>

      <div
        className={classNames(
          'grid gap-3',
          draws.length === 2 ? 'grid-cols-2' : 'grid-cols-3',
        )}
      >
        {draws.map((draw) => (
          <CheckboxImage
            key={draw.draw.id}
            checked={selectedDraws.includes(draw.draw.id)}
            disabled={drawIdsEntered.includes(draw.draw.id)}
            id={draw.draw.id}
            label={draw.product.title}
            name={draw.product.title}
            onChange={() => selectDraw(draw.draw.id)}
            thumbnail={{
              alt: draw.product.title,
              filename: draw.product.thumbnail?.url ?? '',
            }}
            type="checkbox"
            value={draw.draw.id}
          />
        ))}
      </div>
      {/* follow the regular draw flow if only a single one is selected */}
      {/* and it had not been entered yet */}
      {selectedDraws.length === 1 &&
      !drawIdsEntered.includes(selectedDraws[0]) ? (
        <PreAuthDrawButton
          buttonLocation="mainCta"
          drawWithFrames={draws.find((d) => d.draw.id === selectedDraws[0])!}
          startDrawFlow={() => {
            onEnterDraw(
              draws.find((d) => d.draw.id === selectedDraws[0])!,
              false,
            );
          }}
        />
      ) : (
        <Button
          disabled={selectedDraws.length === 0}
          fullWidth
          label={t('drawers.review.draw.cta')}
          onClick={() => {
            goToStep('Complete entries');
          }}
          variant="primary"
        />
      )}
    </div>
  );
};

const EnterDraws = ({
  draws,
  onEnterDraw,
  t,
}: {
  draws: DrawWithFrames[];
  onEnterDraw: (
    drawWithFrames: DrawWithFrames,
    relaunchMultiFlowOnCompletion: boolean,
  ) => void;
  t: TFunction;
}) => {
  const { goToStep } = useFlow();
  const {
    isAccountInDraw,
    selectedDraws,
    setWinningDrawsCount,
    winningDrawsCount,
  } = useDraws();
  const [upsertMultisetPreferencesDraw] =
    useUpsertMultisetPreferencesDrawMutation();

  const handleConfirmWorksCount = () => {
    upsertMultisetPreferencesDraw({
      variables: {
        multisetId: draws[0].draw.multisetID,
        maxQuantity: winningDrawsCount,
      },
    }).then((data) => {
      if (data.data?.upsertMultisetPreferencesDraw) {
        goToStep('success');
      }
    });
  };

  const drawsToEnter = draws.filter((draw) =>
    selectedDraws.includes(draw.draw.id),
  );

  const drawEntries = useSelector((s: any) => s.features.draw.drawEntry);

  const enteredDraws = drawsToEnter.filter((draw) => {
    const drawEntry = drawEntries[draw.draw.id];
    return drawEntry?.entered || isAccountInDraw(draw.draw.id);
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        {drawsToEnter.map((draw) => (
          <div className="flex w-full flex-col rounded-sm border border-divider/20 p-3">
            <div className="flex w-full items-center justify-center space-x-3">
              <div className="flex flex-1 items-center justify-center space-x-3 overflow-hidden">
                <div className="h-12 w-12 shrink-0">
                  <Image
                    alt={`${draw.product.title} by ${draw.product.artists[0].name}`}
                    shape="rounded"
                    src={draw.product.thumbnail?.url ?? ''}
                    width={48}
                  />
                </div>
                <div className="flex flex-1 flex-col overflow-hidden">
                  <span className="truncate font-bold">
                    {draw.product.artists[0].name}
                  </span>
                  <span className="truncate">{draw.product.title}</span>
                </div>
                <PreAuthDrawButton
                  key={draw.draw.id}
                  buttonLocation="dialogCta"
                  drawWithFrames={draw}
                  fullWidth={false}
                  size="md"
                  startDrawFlow={() => {
                    onEnterDraw(draw, true);
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-4">
        <p className="font-bold">
          {t('drawers.review.draw.multiProduct.enterDrawsQuantityTitle')}
        </p>
        <div
          className={classNames('flex gap-3', {
            'grid grid-cols-6': selectedDraws.length >= 6,
          })}
        >
          {Array.from({ length: selectedDraws.length }, (_, index) => (
            <button
              key={index}
              className={classNames(
                'cursor:pointer w-full rounded-sm border p-4 outline outline-2 outline-offset-2 outline-transparent ring-1 transition-all duration-300 focus:outline-[rgba(var(--shade-brown-500),0.5)] disabled:cursor-not-allowed disabled:opacity-50',
                winningDrawsCount === index + 1
                  ? 'border-[rgba(var(--shade-brown-950))] ring-[rgba(var(--shade-brown-950))]'
                  : 'border-[rgba(var(--shade-brown-600),0.2)] ring-transparent',
              )}
              disabled={index + 1 > enteredDraws.length}
              onClick={() => setWinningDrawsCount(index + 1)}
              type="button"
            >
              {index + 1}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between text-sm">
          <span>
            {t(
              'drawers.review.draw.multiProduct.enterDrawsQuantityDescription',
            )}
          </span>
          <span>{enteredDraws.length}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>
            {t('drawers.review.draw.multiProduct.enterDrawsQuantityMax')}
          </span>
          <span>{winningDrawsCount}</span>
        </div>
      </div>

      <Button
        disabled={enteredDraws.length === 0}
        fullWidth
        label={t('drawers.review.draw.multiProduct.confirmButton')}
        onClick={() => handleConfirmWorksCount()}
        variant="primary"
      />
    </div>
  );
};

const isDrawPredicate = (
  draw: DrawWithFrames | undefined,
): draw is DrawWithFrames => Boolean(draw);

const Success = ({ t }: { t: TFunction }) => {
  const { closeFlow } = useFlow();
  const [emitter] = useAnalyticsEmitter();
  const router = useRouter();
  const { drawIdsEntered, linkedDraws, mainDraw, winningDrawsCount } =
    useDraws();
  const mostExpensiveCombination = [mainDraw, ...linkedDraws]
    .filter(isDrawPredicate)
    .filter((draw) => drawIdsEntered.includes(draw.draw.id))
    .map((draw) => draw.product.price)
    .sort((a, b) => b - a)
    .slice(0, winningDrawsCount)
    .reduce((acc, price) => acc + price, 0);
  const isAmlRequired = mostExpensiveCombination > AML_LIMIT;

  const handleDiscoverMoreRedirect = () => {
    emitter('discover', 'clicked');
    router.push(pathFor({ page: 'WORKS_INDEX' }));
    closeFlow();
  };

  const handleAmlRedirect = () => {
    emitter('AML', 'clicked');
    router.push('/aml');
    closeFlow();
  };

  return (
    <div className="flex flex-col gap-6 text-center">
      <div className="flex flex-col gap-3 text-center">
        <p className="text-md font-bold">
          {t('drawers.review.draw.multiProduct.successTitle')}
        </p>

        {isAmlRequired ? (
          <p className="text-neutral-4">
            {t('drawers.review.draw.multiProduct.successDescriptionAml', {
              artist: mainDraw?.product.artists[0].name,
              enteredCount: drawIdsEntered.length.toString(),
              winCount:
                winningDrawsCount === drawIdsEntered.length
                  ? 'all'
                  : `up to ${winningDrawsCount}`,
            })}
          </p>
        ) : (
          <p className="text-neutral-4">
            {t('drawers.review.draw.multiProduct.successDescription', {
              artist: mainDraw?.product.artists[0].name,
              enteredCount: drawIdsEntered.length.toString(),
              winCount:
                winningDrawsCount === drawIdsEntered.length
                  ? 'all'
                  : `up to ${winningDrawsCount}`,
            })}
          </p>
        )}
      </div>

      {isAmlRequired ? (
        <Button
          fullWidth
          label={t('drawers.review.draw.multiProduct.getVerified')}
          onClick={handleAmlRedirect}
          variant="primary"
        />
      ) : (
        <Button
          fullWidth
          label={t('drawers.review.draw.multiProduct.discoverButton')}
          onClick={handleDiscoverMoreRedirect}
          variant="primary"
        />
      )}
    </div>
  );
};

const buildMultiProductDrawEntryFlows = (
  t: TFunction,
  mainDraw: DrawWithFrames,
  linkedDraws: DrawWithFrames[],
  onEnterDraw: (
    drawWithFrames: DrawWithFrames,
    relaunchMultiFlowOnCompletion: boolean,
  ) => void,
): FlowConfig => {
  const allDraws = [mainDraw, ...linkedDraws];

  return {
    name: 'select works',
    containerId: 'multi-product-draw',
    steps: [
      {
        id: 'select works',
        title: t('drawers.review.draw.multiProduct.selectWorkTitle'),
        hideBackButton: true,
        analytics: {
          name: 'select works',
          properties: {
            'product title': mainDraw?.product.title,
          },
        },
        content: (
          <SelectWorks draws={allDraws} onEnterDraw={onEnterDraw} t={t} />
        ),
      },
      {
        id: 'Complete entries',
        title: t('drawers.review.draw.multiProduct.enterDrawsTitle'),
        analytics: {
          name: 'Complete entries',
          properties: {
            'product title': mainDraw?.product.title,
          },
        },
        content: (
          <EnterDraws draws={allDraws} onEnterDraw={onEnterDraw} t={t} />
        ),
      },
      {
        id: 'success',
        hideBackButton: true,
        analytics: {
          name: 'success',
          properties: {
            'product title': mainDraw?.product.title,
          },
        },
        content: <Success t={t} />,
      },
    ],
  };
};

export const MultiProductDrawFlow = ({
  linkedDraws,
  mainDraw,
  t,
}: {
  mainDraw: DrawWithFrames;
  linkedDraws: DrawWithFrames[];
  t: TFunction;
}) => {
  const { isLoggedIn } = useAuth();

  const { draw, product } = mainDraw;
  const [waitForAuthAndStartDrawFlow, setWaitForAuthAndStartDrawFlow] =
    useState(false);

  const mainDrawIsPreOrder = useIsPreOrder(draw);

  const { closeFlow, startFlow } = useFlow();
  const [emitter] = useAnalyticsEmitter();
  const { authenticateAndSubscribe } = useAuthenticate();

  const { resetController } = usePreAuthDrawCtx();

  const drawVariant = product.variants.find((v) => v.title === 'Draw');

  const subscriptions = buildSubscriptions({
    'artists':
      product.artists.map((a) => ({
        name: a.name,
        internalId: a.id,
      })) || [],
    'isPreOrder': mainDrawIsPreOrder,
    'productId': draw.productId ?? '',
    'productRef': skuRef(drawVariant?.sku ?? ''),
    'productTitle': product.title ?? '',
  });

  const { isSubscribedAll, refetch } = useIsSubscribed(
    subscriptions.map(({ objectId, type }) => ({ objectId, type })),
  );

  const handleEnterSingleDraw = useCallback(
    (
      drawWithFrames: DrawWithFrames,
      relaunchMultiFlowOnCompletion: boolean = true,
    ) => {
      closeFlow();

      setTimeout(() => {
        const preOrder = isPreOrder(getDrawStage(drawWithFrames.draw));

        const singleDrawFlowConfig = buildDrawFlowConfig(
          t,
          drawWithFrames,
          preOrder,
          false,
        );

        const relaunchMultiFlow = () => {
          resetController();

          if (!relaunchMultiFlowOnCompletion) {
            return;
          }

          const multiFlowConfig = buildMultiProductDrawEntryFlows(
            t,
            mainDraw,
            linkedDraws,
            handleEnterSingleDraw,
          );

          startFlow({
            ...multiFlowConfig,
            startStepId: 'Complete entries',
          });
        };

        startFlow({
          ...singleDrawFlowConfig,
          onFlowComplete: relaunchMultiFlow,
        });
      }, 550); // The flow dialog has a timeout of 500ms to close, so we add a buffer of 50ms to open a new one
    },
    [closeFlow, linkedDraws, mainDraw, resetController, startFlow, t],
  );

  const startMultiProductDrawFlow = useCallback(() => {
    startFlow(
      buildMultiProductDrawEntryFlows(
        t,
        mainDraw,
        linkedDraws,
        handleEnterSingleDraw,
      ),
    );
  }, [startFlow, t, mainDraw, linkedDraws, handleEnterSingleDraw]);

  const hasFlowStarted = useRef(false);

  useEffect(() => {
    if (waitForAuthAndStartDrawFlow && isLoggedIn && !hasFlowStarted.current) {
      hasFlowStarted.current = true;
      setWaitForAuthAndStartDrawFlow(false);
      startMultiProductDrawFlow();
      refetch();
    }
  }, [
    waitForAuthAndStartDrawFlow,
    isLoggedIn,
    refetch,
    startMultiProductDrawFlow,
  ]);

  const buttonClicked = useCallback(() => {
    if (isLoggedIn && isSubscribedAll) {
      startMultiProductDrawFlow();
      return;
    }
    authenticateAndSubscribe({
      authScreenThumbnail: product.thumbnail?.url,
      authScreenTitle: mainDrawIsPreOrder
        ? t('authentication:dynamicTitle.preorder')
        : t('authentication:dynamicTitle.draw'),
      analyticsProperties: {
        'artist': product.artists[0].name ?? '',
        'artist name': product.artists[0].name ?? '',
        'button_location': 'product page',
        'product_id': draw.productId ?? '',
        'product_status': 'coming soon',
        'product_title': product.title ?? '',
        'source': mainDrawIsPreOrder
          ? AuthEntryPoint.PreOrder
          : AuthEntryPoint.DrawEntry,
      },
      hideSuccessScreen: true,
      onFlowComplete: () => {
        setWaitForAuthAndStartDrawFlow(true);
      },
      optInGeneral: false,
      subscriptions,
    });
  }, [
    authenticateAndSubscribe,
    draw.productId,
    isLoggedIn,
    isSubscribedAll,
    mainDrawIsPreOrder,
    product.artists,
    product.thumbnail?.url,
    product.title,
    refetch,
    startMultiProductDrawFlow,
    subscriptions,
    t,
  ]);

  return (
    <Button
      disabled={isPast(new Date(mainDraw.draw.closeAt))}
      fullWidth
      label={t('drawers.review.draw.multiProduct.buttonTitle')}
      onClick={() => {
        emitter('multi product draw', 'clicked', {
          'product title': mainDraw.product.title,
          'artist name': mainDraw.product.artists[0].name,
          'product price': mainDraw.product.price,
        });
        buttonClicked();
      }}
      variant="primary"
    />
  );
};
